import type { ComputedRef, Ref } from 'vue'
import type {
  AppSelectOption,
  SelectPaginatedData,
} from '@brighterly/lib-core-types/ui/selects/interfaces'

export interface AppSelectPaginatedFetcherParams {
  query: string
  page: number
  signal: AbortSignal
}

export type AppSelectPaginatedFetcher = (
  params: AppSelectPaginatedFetcherParams,
) => Promise<SelectPaginatedData | null>

export interface AppSelectPaginatedOptions {
  lazy?: boolean
  initialBrowseData?:
    | SelectPaginatedData
    | null
    | (() => SelectPaginatedData | null)
}

export interface AppSelectPaginatedReturn {
  options: ComputedRef<AppSelectOption[]>
  hasMore: ComputedRef<boolean>
  loading: Readonly<Ref<boolean>>
  onSearch: (query: string) => void
  onLoadMore: (query: string) => void
  onOpen: () => void
  refresh: () => Promise<void>
}

export const useAppSelectPaginated = (
  fetcher: AppSelectPaginatedFetcher,
  options?: AppSelectPaginatedOptions,
): AppSelectPaginatedReturn => {
  const browseOptions = ref<AppSelectOption[]>([])
  const browsePage = ref<number>(0)
  const browseHasMore = ref<boolean>(false)
  const browseLoaded = ref<boolean>(false)
  const searchOptions = ref<AppSelectOption[]>([])
  const searchPage = ref<number>(0)
  const searchHasMore = ref<boolean>(false)
  const activeQuery = ref<string>('')
  const loading = ref<boolean>(false)
  const isInitialized = ref<boolean>(false)

  let requestId = 0
  let abortController: AbortController | null = null

  const optionsView = computed<AppSelectOption[]>(() =>
    activeQuery.value === '' ? browseOptions.value : searchOptions.value,
  )

  const hasMore = computed<boolean>(() =>
    activeQuery.value === '' ? browseHasMore.value : searchHasMore.value,
  )

  const performFetch = async (
    query: string,
    page: number,
    mode: 'browse' | 'search',
  ): Promise<void> => {
    abortController?.abort()
    abortController = new AbortController()
    const myController = abortController
    const myId = ++requestId

    loading.value = true

    try {
      const data = await fetcher({
        query,
        page,
        signal: myController.signal,
      })

      if (myId !== requestId) return
      if (data === null) return

      if (mode === 'browse') {
        browseOptions.value = page === 1
          ? [...data.items]
          : [...browseOptions.value, ...data.items]
        browsePage.value = page
        browseHasMore.value = !!data.meta.next_page_url
        if (page === 1) browseLoaded.value = true
      } else {
        searchOptions.value = page === 1
          ? [...data.items]
          : [...searchOptions.value, ...data.items]
        searchPage.value = page
        searchHasMore.value = !!data.meta.next_page_url
      }
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return
      if (myId !== requestId) return
    } finally {
      if (myId === requestId) loading.value = false
    }
  }

  // ── Seed application helper ───────────────────────────────────
  const applySeed = (data: SelectPaginatedData): void => {
    browseOptions.value = [...data.items]
    browsePage.value = 1
    browseHasMore.value = !!data.meta.next_page_url
    browseLoaded.value = true
    isInitialized.value = true
  }

  let awaitingSeed = false

  const seedSource = options?.initialBrowseData

  if (typeof seedSource === 'function') {
    const initial = seedSource()
    if (initial) {
      applySeed(initial)
    }
    else {
      awaitingSeed = true
      const stop = watch(seedSource, (data) => {
        if (data && !isInitialized.value) {
          applySeed(data)
          awaitingSeed = false
          stop()
        }
      })
    }
  }
  else if (seedSource) {
    applySeed(seedSource)
  }
  else if (!options?.lazy) {
    isInitialized.value = true
    void performFetch('', 1, 'browse')
  }

  const onSearch = (query: string): void => {
    if (query === '') {
      activeQuery.value = ''
      if (!browseLoaded.value) {
        void performFetch('', 1, 'browse')
      }
      return
    }
    activeQuery.value = query
    searchOptions.value = []
    searchPage.value = 0
    searchHasMore.value = false
    void performFetch(query, 1, 'search')
  }
  const onLoadMore = (query: string): void => {
    if (loading.value) return
    if (query === '') {
      if (!browseHasMore.value) return
      void performFetch('', browsePage.value + 1, 'browse')
      return
    }
    if (!searchHasMore.value) return
    void performFetch(query, searchPage.value + 1, 'search')
  }
  const onOpen = (): void => {
    if (isInitialized.value) return
    if (awaitingSeed) return
    isInitialized.value = true
    void performFetch('', 1, 'browse')
  }
  const refresh = async (): Promise<void> => {
    abortController?.abort()
    browseOptions.value = []
    browsePage.value = 0
    browseHasMore.value = false
    browseLoaded.value = false
    searchOptions.value = []
    searchPage.value = 0
    searchHasMore.value = false
    activeQuery.value = ''
    isInitialized.value = true
    await performFetch('', 1, 'browse')
  }

  onBeforeUnmount(() => {
    abortController?.abort()
  })

  return {
    options: optionsView,
    hasMore,
    loading,
    onSearch,
    onLoadMore,
    onOpen,
    refresh,
  }
}
